import { useEffect, useState } from "react";
import { adminApi, type AdminUser, type LedgerEntry, type Pagination } from "../../api/admin";
import { StatusPill } from "../../components/StatusPill";
import { Modal } from "../../components/Modal";
import { AdminConfirmDialog, AdminEmpty, AdminError, AdminNotice, AdminPager, AdminSkeleton, AdminToolbar, type ConfirmState, useDebouncedValue } from "../../components/admin/AdminUi";

export function AdminUsersPage() {
  const [items, setItems] = useState<AdminUser[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query);
  const [filterStatus, setFilterStatus] = useState("");
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [notice, setNotice] = useState<{ tone: "success" | "error"; message: string } | null>(null);
  const [creditTarget, setCreditTarget] = useState<AdminUser | null>(null);
  const [creditAmount, setCreditAmount] = useState(0);
  const [creditReason, setCreditReason] = useState("");
  const [creditSaving, setCreditSaving] = useState(false);
  const [creditError, setCreditError] = useState("");
  const [ledgerTarget, setLedgerTarget] = useState<AdminUser | null>(null);
  const [ledger, setLedger] = useState<LedgerEntry[]>([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    adminApi.users({ page, pageSize: 25, q: debouncedQuery, status: filterStatus, role })
      .then((response) => { if (active) { setItems(response.items); setPagination(response.pagination); setSelected(new Set()); } })
      .catch((reason: unknown) => { if (active) setError(reason instanceof Error ? reason.message : "用户列表加载失败"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [debouncedQuery, filterStatus, page, revision, role]);

  useEffect(() => { setPage(1); }, [debouncedQuery, filterStatus, role]);

  function setStatus(ids: number[], target: "active" | "disabled") {
    setConfirm({
      title: target === "disabled" ? "停用用户" : "恢复用户",
      description: `将对 ${ids.length} 个用户执行${target === "disabled" ? "停用" : "启用"}。停用后其当前会话会在下一次请求时失效。`,
      confirmLabel: target === "disabled" ? "确认停用" : "确认启用",
      tone: target === "disabled" ? "danger" : "default",
      onConfirm: async (reason) => {
        const result = await adminApi.setUsersStatus(ids, target, reason);
        setNotice({ tone: "success", message: `已更新 ${result.changed_ids.length} 个用户` });
        setRevision((value) => value + 1);
      },
    });
  }

  function openLedger(user: AdminUser) {
    setLedgerTarget(user);
    setLedgerLoading(true);
    setLedger([]);
    adminApi.ledger(user.id).then((response) => setLedger(response.items)).catch((reason: unknown) => setNotice({ tone: "error", message: reason instanceof Error ? reason.message : "流水加载失败" })).finally(() => setLedgerLoading(false));
  }

  async function submitCredit() {
    if (!creditTarget || creditAmount === 0 || creditReason.trim().length < 3) {
      setCreditError("请输入非零积分和至少 3 个字符的原因");
      return;
    }
    setCreditSaving(true);
    setCreditError("");
    try {
      const result = await adminApi.adjustCredits(creditTarget.id, creditAmount, creditReason.trim());
      setNotice({ tone: "success", message: `${creditTarget.username} 的余额已更新为 ${result.balance}` });
      setCreditTarget(null);
      setRevision((value) => value + 1);
    } catch (reason) {
      setCreditError(reason instanceof Error ? reason.message : "积分调整失败");
    } finally {
      setCreditSaving(false);
    }
  }

  const selectedIds = [...selected];
  return (
    <div className="admin-page">
      <header className="admin-page__heading"><div><span>IDENTITY & BALANCE</span><h2>用户与积分</h2><p>分页检索用户，停用账号并追踪每次积分变化。</p></div></header>
      <AdminToolbar>
        <label className="admin-search"><span>搜索</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="用户名或用户 ID" /></label>
        <label><span>状态</span><select value={filterStatus} onChange={(event) => setFilterStatus(event.target.value)}><option value="">全部状态</option><option value="active">活跃</option><option value="disabled">已停用</option></select></label>
        <label><span>角色</span><select value={role} onChange={(event) => setRole(event.target.value)}><option value="">全部角色</option><option value="user">用户</option><option value="admin">管理员</option></select></label>
        {selectedIds.length ? <div className="admin-batch-actions"><b>已选 {selectedIds.length}</b><button onClick={() => setStatus(selectedIds, "active")}>启用</button><button className="is-danger" onClick={() => setStatus(selectedIds, "disabled")}>停用</button></div> : null}
      </AdminToolbar>
      {error ? <AdminError message={error} retry={() => setRevision((value) => value + 1)} /> : null}
      <section className="admin-panel admin-table-panel">
        {loading ? <AdminSkeleton rows={7} /> : items.length ? (
          <div className="admin-table-scroll"><table className="admin-table"><thead><tr><th><input type="checkbox" aria-label="选择本页" checked={items.length > 0 && selected.size === items.length} onChange={(event) => setSelected(event.target.checked ? new Set(items.map((item) => item.id)) : new Set())} /></th><th>用户</th><th>角色 / 状态</th><th>积分</th><th>任务</th><th>创建时间</th><th>操作</th></tr></thead><tbody>{items.map((user) => <tr key={user.id}><td><input type="checkbox" checked={selected.has(user.id)} onChange={() => setSelected((current) => { const next = new Set(current); next.has(user.id) ? next.delete(user.id) : next.add(user.id); return next; })} /></td><td><b>{user.username}</b><small>UID {user.id}</small></td><td><span className="admin-role">{user.role}</span><StatusPill status={user.status} /></td><td><strong>{user.credit_balance.toLocaleString()}</strong></td><td>{user.task_count}</td><td>{new Date(user.created_at).toLocaleDateString()}</td><td><div className="admin-row-actions"><button onClick={() => { setCreditTarget(user); setCreditAmount(0); setCreditReason(""); setCreditError(""); }}>调分</button><button onClick={() => openLedger(user)}>流水</button><button className={user.status === "active" ? "is-danger" : ""} onClick={() => setStatus([user.id], user.status === "active" ? "disabled" : "active")}>{user.status === "active" ? "停用" : "启用"}</button></div></td></tr>)}</tbody></table></div>
        ) : <AdminEmpty title="没有匹配用户" description="尝试清空搜索词或更换筛选条件。" />}
        {pagination ? <AdminPager pagination={pagination} onPage={setPage} /> : null}
      </section>

      {creditTarget ? <Modal open title={`调整 ${creditTarget.username} 的积分`} eyebrow="积分变更" description={`当前余额 ${creditTarget.credit_balance}。负数表示扣减，系统不允许扣成负余额。`} busy={creditSaving} onClose={() => setCreditTarget(null)}><section className="admin-dialog admin-dialog--embedded"><label>调整数量<input data-autofocus type="number" value={creditAmount} onChange={(event) => setCreditAmount(Number(event.target.value))} /></label><label>操作原因<textarea value={creditReason} onChange={(event) => setCreditReason(event.target.value)} maxLength={255} /></label>{creditError ? <div className="admin-dialog__error">{creditError}</div> : null}<footer><button className="button button--quiet" disabled={creditSaving} onClick={() => setCreditTarget(null)}>取消</button><button className="button button--primary" disabled={creditSaving} onClick={() => void submitCredit()}>{creditSaving ? <span className="button-spinner" /> : null}提交调整</button></footer></section></Modal> : null}

      {ledgerTarget ? <Modal open wide title={`${ledgerTarget.username} 的积分流水`} eyebrow="LEDGER" description="按时间倒序显示最近 25 条积分变更。" onClose={() => setLedgerTarget(null)}><div>{ledgerLoading ? <AdminSkeleton rows={6} /> : ledger.length ? <div className="admin-ledger-list">{ledger.map((entry) => <article key={entry.id}><span className={entry.amount >= 0 ? "is-positive" : "is-negative"}>{entry.amount >= 0 ? "+" : ""}{entry.amount}</span><div><b>{entry.remark || entry.type}</b><small>{new Date(entry.created_at).toLocaleString()} · 余额 {entry.balance_after}</small></div></article>)}</div> : <AdminEmpty title="暂无流水" description="这个用户还没有积分变更记录。" />}</div></Modal> : null}
      <AdminConfirmDialog state={confirm} close={() => setConfirm(null)} />
      <AdminNotice notice={notice} clear={() => setNotice(null)} />
    </div>
  );
}
